import { useState, useEffect } from 'react';

const COLLAPSE_STORAGE_KEY = 'leftMenu:collapsed';

export const useLeftMenuState = (isMobile: boolean) => {
  // Start with menu open in desktop mode, closed in mobile
  const [isOpen, setIsOpen] = useState(!isMobile);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      const stored = localStorage.getItem(COLLAPSE_STORAGE_KEY);
      return stored === 'true';
    } catch {
      return false;
    }
  });
  const [profileAnchorEl, setProfileAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);

  // Important: This useEffect ensures the menu state updates when
  // the device type changes (e.g., when resizing the browser window)
  useEffect(() => {
    // Close the drawer immediately when switching to mobile to prevent backdrop issues
    if (isMobile) {
      setIsOpen(false);
      setIsCollapsed(false);
    } else {
      // Open the drawer when switching to desktop
      setIsOpen(true);
    }
  }, [isMobile]);

  // Toggle drawer open/closed
  const toggleDrawer = () => setIsOpen((prev) => !prev);

  // Collapse handlers (desktop only)
  const toggleCollapse = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSE_STORAGE_KEY, String(next));
      } catch {}
      return next;
    });
  };

  // Profile menu handlers
  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => setProfileAnchorEl(null);

  // Settings modal handlers
  const handleSettingsOpen = () => {
    handleProfileClose();
    setSettingsModalOpen(true);
  };

  const handleSettingsClose = () => setSettingsModalOpen(false);

  return {
    isOpen,
    isCollapsed,
    profileAnchorEl,
    settingsModalOpen,
    setIsOpen,
    toggleDrawer,
    toggleCollapse,
    handleProfileClick,
    handleProfileClose,
    handleSettingsOpen,
    handleSettingsClose
  };
};
